import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface TutorialCardProps {
  id: string;
  title: string;
  description: string;
  image?: string;
  tags: string[];
  difficulty: string;
  duration: number;
  techStack: string[];
  dateAdded: string;
  isActive?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
}

const TutorialCard = ({
  id,
  title,
  description,
  image,
  tags,
  difficulty,
  duration,
  techStack,
  dateAdded,
  isActive,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: TutorialCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const showHoverEffect = isActive !== undefined ? isActive : isHovered;

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative border border-white overflow-hidden transition-all duration-700 cursor-pointer block",
        showHoverEffect && "md:scale-[1.03] z-10"
      )}
      onMouseEnter={() => {
        setIsHovered(true);
        onMouseEnter?.();
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onMouseLeave?.();
      }}
      onClick={onClick}
      tabIndex={0}
      role="button"
      aria-label={title}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        {image && (
          <img
            src={image}
            alt={title}
            className={cn(
              "w-full h-full object-cover transition-transform duration-700",
              showHoverEffect ? "scale-110" : "scale-100"
            )}
          />
        )}
        <div
          className={cn(
            "absolute inset-0 bg-black transition-opacity duration-500",
            showHoverEffect ? "opacity-30" : "opacity-50"
          )}
        ></div>
      </div>

      {/* Content overlay */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between transition-all duration-500">
        {/* Top content - Tags */}
        <div className="flex flex-wrap gap-2 mb-2">
          <Badge variant="secondary">{difficulty}</Badge>
          <Badge variant="outline">{duration} min</Badge>
          {techStack.slice(0, 2).map((tech, i) => (
            <Badge key={i} variant="outline">
              {tech}
            </Badge>
          ))}
        </div>

        {/* Bottom content */}
        <div
          className={cn(
            "transform transition-all duration-700",
            showHoverEffect
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-80"
          )}
        >
          <h3
            className={cn(
              "font-display text-2xl md:text-3xl mb-2 transition-transform duration-700",
              showHoverEffect ? "translate-x-0" : "-translate-x-4"
            )}
          >
            {title}
          </h3>

          <p
            className={cn(
              "text-sm max-w-md transition-all duration-700 delay-100",
              showHoverEffect
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-4"
            )}
          >
            {description}
          </p>

          <div
            className={cn(
              "mt-4 inline-block transition-all duration-700 delay-200",
              showHoverEffect
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            )}
          >
            <span className="text-sm uppercase tracking-wider border-b border-white pb-1 transition-all duration-300 group-hover:border-opacity-100">
              View Tutorial
            </span>
          </div>
        </div>
      </div>

      {/* Animated corners */}
      <div
        className={cn(
          "absolute top-0 left-0 w-8 h-8 border-t border-l border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "top left" }}
      ></div>
      <div
        className={cn(
          "absolute top-0 right-0 w-8 h-8 border-t border-r border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "top right", transitionDelay: "100ms" }}
      ></div>
      <div
        className={cn(
          "absolute bottom-0 left-0 w-8 h-8 border-b border-l border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "bottom left", transitionDelay: "100ms" }}
      ></div>
      <div
        className={cn(
          "absolute bottom-0 right-0 w-8 h-8 border-b border-r border-white transition-all duration-500",
          showHoverEffect ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}
        style={{ transformOrigin: "bottom right", transitionDelay: "200ms" }}
      ></div>
    </div>
  );
};

export default TutorialCard;
